import React, { useState, useEffect, useRef } from 'react';
import { Progress, Alert } from 'antd';
import { useTranslation } from 'react-i18next';

const LoadingProgress = ({ durationSeconds = 300, startedAt = null }) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);

  const calculateProgress = () => {
    if (startedAt) {
      const now = Date.now();
      const startedAtTimestamp = new Date(startedAt).getTime();
      const elapsedSeconds = (now - startedAtTimestamp) / 1000;
      const baseProgress = Math.min(99, (elapsedSeconds / durationSeconds) * 100);
      
      // Only add random factor if progress is below 99% to prevent flickering
      if (baseProgress < 99) {
        const randomFactor = Math.random() * 0.1 - 0.05; // ±5% randomness
        return Math.max(0, Math.min(99, baseProgress + randomFactor));
      }
      
      return baseProgress;
    } else {
      // Fallback to countdown timer if no startedAt provided
      const baseProgress = Math.min(99, progress + (99 / durationSeconds));
      
      // Only add random factor if progress is below 99%
      if (baseProgress < 99) {
        const randomFactor = Math.random() * 0.1 - 0.05;
        return Math.max(0, Math.min(99, baseProgress + randomFactor));
      }
      
      return baseProgress;
    }
  };

  // Calculate initial progress immediately
  useEffect(() => {
    if (startedAt) {
      setProgress(calculateProgress());
    }
  }, [startedAt, durationSeconds]);

  useEffect(() => {
    // Start progress calculation timer
    intervalRef.current = setInterval(() => {
      setProgress(calculateProgress());
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [durationSeconds, startedAt]);

  return (
    <div style={{ marginTop: '30px' }}>
      <Progress 
        percent={progress} 
        size="large"
        strokeColor="#1890ff"
        showInfo={false}
        style={{ maxWidth: '300px', margin: '0 auto' }}
      />
      <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
        {`${Math.round(progress)}% ${t('loading_progress.progress_bar_text')}`}
      </p>
      {progress >= 99 && (
        <Alert
          message={t('loading_progress.taking_longer')}
          type="info"
          style={{ marginTop: '8px' }}
        />
      )}
    </div>
  );
};

export default LoadingProgress; 